/**
 * Teleconsultation Component - Daily.co Video Integration
 * Video consultation interface for veterinary appointments
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Video, VideoOff, Mic, MicOff, Phone, PhoneOff, MessageSquare, Users, Settings, Maximize2, Minimize2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TeleconsultationRoom = ({ roomName, token, userName, isOwner, onEnd }) => {
  const callFrameRef = useRef(null);
  const containerRef = useRef(null);
  const [callFrame, setCallFrame] = useState(null);
  const [isVideoOn, setIsVideoOn] = useState(true);
  const [isAudioOn, setIsAudioOn] = useState(true);
  const [participants, setParticipants] = useState([]);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isJoined, setIsJoined] = useState(false);
  const [frameReady, setFrameReady] = useState(false);

  // Separate effect to mark when container is mounted
  useEffect(() => {
    if (containerRef.current) {
      setFrameReady(true);
    }
  }, []);

  useEffect(() => {
    // Don't start until container is ready
    if (!frameReady || !containerRef.current) {
      return;
    }

    let frame = null;
    let isMounted = true;
    let joinTimeout = null;
    
    const loadAndJoin = async () => {
      // Load Daily.co script if not already loaded
      if (!window.DailyIframe) {
        const existingScript = document.querySelector('script[src*="daily-js"]');
        if (!existingScript) {
          console.log('Loading Daily.co script...');
          try {
            await new Promise((resolve, reject) => {
              const script = document.createElement('script');
              script.src = 'https://unpkg.com/@daily-co/daily-js';
              script.async = true;
              script.onload = resolve;
              script.onerror = reject;
              document.body.appendChild(script);
            });
            console.log('Daily.co script loaded');
          } catch (err) {
            console.error('Failed to load Daily.co script:', err);
            toast.error('Erreur de chargement du service vidéo');
            setIsLoading(false);
            return;
          }
        } else {
          console.log('Waiting for existing Daily.co script...');
          await new Promise(resolve => {
            if (window.DailyIframe) resolve();
            else existingScript.addEventListener('load', resolve);
          });
        }
      }
      
      if (!isMounted || !containerRef.current) return;
      
      // Set a timeout for joining
      joinTimeout = setTimeout(() => {
        if (isMounted && isLoading) {
          console.log('Join timeout - taking too long');
          toast.error('La connexion prend trop de temps. Vérifiez votre connexion internet.');
          setIsLoading(false);
        }
      }, 30000);
      
      try {
        console.log('Creating Daily frame in container:', containerRef.current);
        
        // Create frame with the dedicated container
        frame = window.DailyIframe.createFrame(containerRef.current, {
          iframeStyle: {
            position: 'absolute',
            top: '0',
            left: '0',
            width: '100%',
            height: '100%',
            border: '0',
            borderRadius: '12px',
          },
          showLeaveButton: true,
          showFullscreenButton: true,
        });

        // Store frame reference immediately
        callFrameRef.current = frame;

        frame.on('joined-meeting', () => {
          console.log('Daily: Successfully joined meeting');
          clearTimeout(joinTimeout);
          if (isMounted) {
            setIsLoading(false);
            setIsJoined(true);
            setCallFrame(frame);
            toast.success('Connecté à la téléconsultation');
          }
        });

        frame.on('participant-joined', (event) => {
          console.log('Daily: Participant joined', event);
          if (isMounted && callFrameRef.current) {
            setParticipants(Object.values(callFrameRef.current.participants()));
            toast.info(`${event.participant.user_name || 'Un participant'} a rejoint`);
          }
        });

        frame.on('participant-left', (event) => {
          console.log('Daily: Participant left', event);
          if (isMounted && callFrameRef.current) {
            setParticipants(Object.values(callFrameRef.current.participants()));
          }
        });

        frame.on('left-meeting', () => {
          console.log('Daily: Left meeting');
          clearTimeout(joinTimeout);
          if (isMounted && isJoined) {
            toast.info('Vous avez quitté la consultation');
            if (onEnd) onEnd();
          }
        });

        frame.on('error', (event) => {
          console.error('Daily error:', event);
          clearTimeout(joinTimeout);
          if (isMounted) {
            toast.error('Erreur vidéo: ' + (event.errorMsg || 'Vérifiez caméra/micro'));
            setIsLoading(false);
          }
        });

        const roomUrl = `https://vetordv.daily.co/${roomName}`;
        console.log('Joining room:', roomUrl);
        
        await frame.join({
          url: roomUrl,
          userName: userName || 'Participant',
        });
        
        console.log('Join call completed');
      } catch (error) {
        console.error('Error initializing call:', error);
        clearTimeout(joinTimeout);
        if (isMounted) {
          toast.error('Impossible de rejoindre: ' + error.message);
          setIsLoading(false);
        }
      }
    };
    
    loadAndJoin();

    // Cleanup function - properly destroy Daily frame before React unmounts
    return () => {
      console.log('Cleanup: destroying frame');
      isMounted = false;
      if (joinTimeout) clearTimeout(joinTimeout);
      
      // Important: destroy the frame synchronously before React removes the container
      const currentFrame = callFrameRef.current;
      if (currentFrame) {
        try {
          currentFrame.leave().catch(() => {});
          currentFrame.destroy();
        } catch (e) {
          console.log('Frame cleanup error (safe to ignore):', e);
        }
        callFrameRef.current = null;
      }
    };
  }, [frameReady, roomName, userName, isLoading, isJoined, onEnd]);

  const toggleVideo = () => {
    if (callFrame) {
      callFrame.setLocalVideo(!isVideoOn);
      setIsVideoOn(!isVideoOn);
    }
  };

  const toggleAudio = () => {
    if (callFrame) {
      callFrame.setLocalAudio(!isAudioOn);
      setIsAudioOn(!isAudioOn);
    }
  };

  const leaveCall = async () => {
    if (callFrame) {
      await callFrame.leave();
    }
    if (onEnd) onEnd();
  };

  const endCallForAll = async () => {
    if (!isOwner) return;
    
    try {
      const authToken = localStorage.getItem('vetToken') || localStorage.getItem('token');
      await fetch(`${API_URL}/api/teleconsultation/end/${roomName}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
        },
      });
      
      if (callFrame) {
        await callFrame.leave();
      }
      toast.success('Téléconsultation terminée');
      if (onEnd) onEnd();
    } catch (error) {
      console.error('Error ending call:', error);
      toast.error('Erreur lors de la fermeture');
    }
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      containerRef.current?.parentElement?.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  return (
    <div className="flex flex-col h-full bg-gray-900 rounded-xl overflow-hidden">
      {/* Video Container - separate container for Daily.co iframe */}
      <div className="flex-1 relative">
        {/* Dedicated container for Daily.co - never has React children */}
        <div 
          ref={containerRef} 
          className="absolute inset-0"
          data-testid="daily-video-container"
        />
        {/* Loading overlay - separate from Daily container */}
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-900 z-20 pointer-events-none">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mx-auto mb-4"></div>
              <p className="text-white">Connexion en cours...</p>
            </div>
          </div>
        )}
      </div>

      {/* Controls */}
      <div className="bg-gray-800 p-4">
        <div className="flex items-center justify-center gap-4">
          {/* Video Toggle */}
          <Button
            variant={isVideoOn ? "secondary" : "destructive"}
            size="lg"
            className="rounded-full w-14 h-14"
            onClick={toggleVideo}
            data-testid="toggle-video-btn"
          >
            {isVideoOn ? <Video className="h-6 w-6" /> : <VideoOff className="h-6 w-6" />}
          </Button>

          {/* Audio Toggle */}
          <Button
            variant={isAudioOn ? "secondary" : "destructive"}
            size="lg"
            className="rounded-full w-14 h-14"
            onClick={toggleAudio}
            data-testid="toggle-audio-btn"
          >
            {isAudioOn ? <Mic className="h-6 w-6" /> : <MicOff className="h-6 w-6" />}
          </Button>

          {/* Leave Call */}
          <Button
            variant="destructive"
            size="lg"
            className="rounded-full w-14 h-14 bg-red-600 hover:bg-red-700"
            onClick={leaveCall}
            data-testid="leave-call-btn"
          >
            <PhoneOff className="h-6 w-6" />
          </Button>

          {/* End Call (Owner only) */}
          {isOwner && (
            <Button
              variant="destructive"
              size="lg"
              className="rounded-full px-6 h-14 bg-red-700 hover:bg-red-800"
              onClick={endCallForAll}
              data-testid="end-call-btn"
            >
              <Phone className="h-5 w-5 mr-2" />
              Terminer
            </Button>
          )}

          {/* Fullscreen */}
          <Button
            variant="secondary"
            size="lg"
            className="rounded-full w-14 h-14"
            onClick={toggleFullscreen}
            data-testid="fullscreen-btn"
          >
            {isFullscreen ? <Minimize2 className="h-6 w-6" /> : <Maximize2 className="h-6 w-6" />}
          </Button>
        </div>

        {/* Participants count */}
        <div className="flex items-center justify-center mt-3 text-gray-400 text-sm">
          <Users className="h-4 w-4 mr-2" />
          {participants.length} participant{participants.length > 1 ? 's' : ''}
        </div>
      </div>
    </div>
  );
};


// Main Teleconsultation Component
const Teleconsultation = ({ appointmentId, patientName, animalName, reason, onClose }) => {
  const [step, setStep] = useState('setup'); // setup, waiting, in_call
  const [roomData, setRoomData] = useState(null);
  const [tokenData, setTokenData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  // Use vetToken for veterinarians, fallback to token for patients
  const authToken = localStorage.getItem('vetToken') || localStorage.getItem('token');

  const createRoom = async () => {
    setIsLoading(true);
    
    // Debug: log the token being used
    console.log('Creating room with token:', authToken ? authToken.substring(0, 20) + '...' : 'NO TOKEN');
    
    if (!authToken) {
      console.error('No auth token found! vetToken:', localStorage.getItem('vetToken'), 'token:', localStorage.getItem('token'));
      toast.error('Erreur: vous devez être connecté');
      setIsLoading(false);
      return;
    }
    
    try {
      console.log('Calling API:', `${API_URL}/api/teleconsultation/create-room`);
      const response = await fetch(`${API_URL}/api/teleconsultation/create-room`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          appointment_id: appointmentId || '',
          patient_name: patientName || 'Patient',
          animal_name: animalName || 'Animal',
          reason: reason || '',
        }),
      });

      console.log('API response status:', response.status);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('API error:', errorData);
        throw new Error(errorData.detail || 'Erreur création salle');
      }

      const data = await response.json();
      setRoomData(data);
      setStep('waiting');
      toast.success('Salle de téléconsultation créée');
      
      // Auto-join after creating
      await joinRoom(data.room_name);
    } catch (error) {
      console.error('Error creating room:', error);
      toast.error('Impossible de créer la salle');
    } finally {
      setIsLoading(false);
    }
  };

  const joinRoom = async (roomName) => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/teleconsultation/join/${roomName}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
        },
      });

      if (!response.ok) {
        throw new Error('Erreur rejoindre salle');
      }

      const data = await response.json();
      setTokenData(data);
      setStep('in_call');
    } catch (error) {
      console.error('Error joining room:', error);
      toast.error('Impossible de rejoindre la salle');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEndCall = () => {
    setStep('setup');
    setRoomData(null);
    setTokenData(null);
    if (onClose) onClose();
  };

  // Setup screen
  if (step === 'setup') {
    return (
      <div className="bg-white rounded-xl p-8 max-w-md mx-auto text-center">
        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <Video className="h-10 w-10 text-green-600" />
        </div>
        <h2 className="text-2xl font-bold text-gray-800 mb-2">Téléconsultation</h2>
        <p className="text-gray-600 mb-6">
          {patientName && animalName 
            ? `Consultation pour ${animalName} (${patientName})`
            : 'Démarrer une nouvelle téléconsultation'}
        </p>
        
        <div className="space-y-4">
          <div className="bg-gray-50 rounded-lg p-4 text-left">
            <h3 className="font-medium text-gray-800 mb-2">Avant de commencer :</h3>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>✓ Vérifiez votre caméra et microphone</li>
              <li>✓ Assurez-vous d'avoir une bonne connexion</li>
              <li>✓ Trouvez un endroit calme et bien éclairé</li>
            </ul>
          </div>
          
          <Button
            onClick={createRoom}
            disabled={isLoading}
            className="w-full bg-green-600 hover:bg-green-700 text-white py-6 text-lg"
            data-testid="start-teleconsultation-btn"
          >
            {isLoading ? (
              <span className="flex items-center">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                Préparation...
              </span>
            ) : (
              <span className="flex items-center justify-center">
                <Video className="h-5 w-5 mr-2" />
                Démarrer la téléconsultation
              </span>
            )}
          </Button>
          
          {onClose && (
            <Button
              variant="outline"
              onClick={onClose}
              className="w-full"
            >
              Annuler
            </Button>
          )}
        </div>
      </div>
    );
  }

  // Waiting screen
  if (step === 'waiting') {
    return (
      <div className="bg-white rounded-xl p-8 max-w-md mx-auto text-center">
        <div className="animate-pulse">
          <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Users className="h-10 w-10 text-blue-600" />
          </div>
        </div>
        <h2 className="text-2xl font-bold text-gray-800 mb-2">Salle d'attente</h2>
        <p className="text-gray-600 mb-6">
          En attente de connexion...
        </p>
        
        {roomData && (
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <p className="text-sm text-gray-600 mb-2">Lien de la consultation :</p>
            <code className="text-xs bg-gray-200 px-2 py-1 rounded break-all">
              {roomData.room_url}
            </code>
          </div>
        )}
        
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mx-auto"></div>
      </div>
    );
  }

  // In call
  if (step === 'in_call' && tokenData && roomData) {
    return (
      <div className="h-[600px] w-full">
        <TeleconsultationRoom
          key={`room-${roomData.room_name}-${Date.now()}`}
          roomName={roomData.room_name}
          token={tokenData.token}
          userName={tokenData.user_name}
          isOwner={tokenData.is_owner}
          onEnd={handleEndCall}
        />
      </div>
    );
  }

  return null;
};


// Patient Join Component
export const PatientTeleconsultationJoin = ({ roomName, onClose }) => {
  const [tokenData, setTokenData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [roomData, setRoomData] = useState(null);
  // Use patientToken for patients (this is how EspacePatientPage stores it)
  const authToken = localStorage.getItem('patientToken') || localStorage.getItem('token');

  const joinRoom = async () => {
    if (!authToken) {
      toast.error('Erreur: vous devez être connecté');
      return;
    }
    
    setIsLoading(true);
    console.log('Patient joining room:', roomName, 'with token:', authToken?.substring(0, 20) + '...');
    
    try {
      const response = await fetch(`${API_URL}/api/teleconsultation/join/${roomName}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Join error:', errorData);
        throw new Error(errorData.detail || 'Erreur rejoindre salle');
      }

      const data = await response.json();
      setTokenData(data);
      setRoomData({ room_name: roomName });
    } catch (error) {
      console.error('Error joining room:', error);
      toast.error('Impossible de rejoindre la consultation');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEndCall = () => {
    setTokenData(null);
    setRoomData(null);
    if (onClose) onClose();
  };

  if (tokenData && roomData) {
    return (
      <div className="h-[600px] w-full">
        <TeleconsultationRoom
          key={`patient-room-${roomData.room_name}-${Date.now()}`}
          roomName={roomData.room_name}
          token={tokenData.token}
          userName={tokenData.user_name}
          isOwner={tokenData.is_owner}
          onEnd={handleEndCall}
        />
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl p-8 max-w-md mx-auto text-center">
      <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
        <Video className="h-10 w-10 text-green-600" />
      </div>
      <h2 className="text-2xl font-bold text-gray-800 mb-2">Rejoindre la téléconsultation</h2>
      <p className="text-gray-600 mb-6">
        Votre vétérinaire vous attend
      </p>
      
      <div className="space-y-4">
        <div className="bg-gray-50 rounded-lg p-4 text-left">
          <h3 className="font-medium text-gray-800 mb-2">Conseils :</h3>
          <ul className="text-sm text-gray-600 space-y-1">
            <li>✓ Autorisez l'accès à la caméra et au micro</li>
            <li>✓ Placez votre animal devant la caméra</li>
            <li>✓ Préparez vos questions</li>
          </ul>
        </div>
        
        <Button
          onClick={joinRoom}
          disabled={isLoading}
          className="w-full bg-green-600 hover:bg-green-700 text-white py-6 text-lg"
          data-testid="join-teleconsultation-btn"
        >
          {isLoading ? (
            <span className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
              Connexion...
            </span>
          ) : (
            <span className="flex items-center justify-center">
              <Video className="h-5 w-5 mr-2" />
              Rejoindre maintenant
            </span>
          )}
        </Button>
        
        {onClose && (
          <Button
            variant="outline"
            onClick={onClose}
            className="w-full"
          >
            Annuler
          </Button>
        )}
      </div>
    </div>
  );
};


export default Teleconsultation;
